import { Component, inject, type OnInit } from '@angular/core';
import { SaveCloseChildItemComponent } from '@app/shared';
import {
  AuthRequestOptions,
  BusinessItem,
  type Item,
  ItemActionResult,
  NtapGlobalConstants,
  Util
} from '@ntap/core';
import {
  catchError,
  finalize,
  first,
  map,
  mergeMap,
  type Observable,
  of,
  from,
  switchMap,
  tap,
  lastValueFrom,
  defer,
  toArray,
  merge
} from 'rxjs';
import { injectUploadProgressStatus } from './file-upload-status';
import { HttpClient } from '@angular/common/http';
import { appConfigService } from '@app/config';

// Odabrano je 6 u batchu jer je to najveći dopušteni broj paralelnih zahtjeva u jednoj HTTP 1.1 vezi u browseru
// Išta više od toga mislim da ne pridonosi ničemu, samo može zagušiti pipe
const BATCH_SIZE = 3;

@Component({
  selector: 'app-spremi-datoteke-action',
  templateUrl: '../../../shared/components/actions/common-action-template.html'
})
export class SpremiDatotekeActionComponent
  extends SaveCloseChildItemComponent
  implements OnInit
{
  private httpClient = inject(HttpClient);
  private appConfig = inject(appConfigService);
  private fileUploadProgressStatus = injectUploadProgressStatus();

  ngOnInit(): void {
    this.setShowMessages(false);
  }

  protected override doExecuteWithItem(
    item: Item
  ): Observable<ItemActionResult> {
    const saveItem = new BusinessItem(item);
    const files: [] = saveItem.data[NtapGlobalConstants.ATTR_FILE_UPLOAD] || [
      {}
    ];
    if (files.length > 0) {
      this.fileUploadProgressStatus.createUploadProgressStatus(files.length);

      // Build array of save observables (one per file)
      const saveObservables = (files as any[]).map((file) =>
        retryAction(() => this.itemViewProvider.save(saveItem)).pipe(
          first(),
          map((result) => ({ result, file }))
        )
      );

      // Run saves in parallel (bounded by BATCH_SIZE) and collect results into array
      return merge(...saveObservables, BATCH_SIZE).pipe(
        toArray(),
        switchMap((allResults: any[]) => {
          const ids = allResults
            .map((r) => {
              const itemType = (r.result as any)[
                NtapGlobalConstants.ATTR_$itemType
              ];
              const idAttr = Util.getFullAttrName(
                itemType,
                NtapGlobalConstants.ATTR_id
              );
              return (r.result as any)[idAttr];
            })
            .filter(Boolean);

          // register batch once; if it errors, abort the whole operation
          return this.registerBatchUpload(ids).pipe(
            switchMap(() => from(allResults))
          );
        }),
        mergeMap((r: any) => {
          const itemActionResult = new ItemActionResult(
            r.result,
            saveItem.data
          );
          const resultItem = Object.assign(
            {},
            itemActionResult.item,
            itemActionResult.result
          ) as Record<string, string>;
          const file = r.file;
          return this.doUploadSingleFile(resultItem, file).pipe(
            tap(() => {
              (itemActionResult.result as any)['en~datoteka~naziv'] = (
                file as File
              ).name;
              this.itemViewProvider.listViewProvider.addItem(
                itemActionResult.result
              );
            }),
            map(() => itemActionResult),
            catchError((err) => {
              this.fileUploadProgressStatus.addErrorFile(
                file,
                JSON.stringify(err)
              );
              return of(null as any);
            }),
            finalize(() => {
              this.fileUploadProgressStatus.incrementDone();
            })
          );
        }, BATCH_SIZE),
        finalize(() => {
          this.notificationService.success('Prijenos datoteka je završio');
          this.refreshParentList();
        })
      );
    }
    return super.doExecuteWithItem(item);
  }

  protected registerBatchUpload(ids: string[] = []) {
    const url = this.appConfig.apiHost + 'batch/upload';
    const payload = { items: ids };
    return this.httpClient.post(url, payload, new AuthRequestOptions()).pipe(
      first(),
      catchError((err) => {
        console.error('Failed to register batch upload', err);
        throw err;
      })
    );
  }

  protected doUploadSingleFile(
    item: { [key: string]: string },
    file: File | object
  ): Observable<unknown> {
    if (!(file instanceof File)) {
      return of({});
    }
    const item_type = item[NtapGlobalConstants.ATTR_$itemType];
    const id_attr = Util.getFullAttrName(
      item_type,
      NtapGlobalConstants.ATTR_id
    );
    return retryAction(() =>
      this.backendUploadService.addContentEx(
        item_type,
        item[id_attr],
        this.getRenditionName(),
        // @ts-expect-error bez storageId
        null,
        file
      )
    ).pipe(
      catchError((err) => {
        console.error('Error uploading file', err);
        let message = `Pogreška prilikom spremanja dokumenta ${file.name}! Molimo, pokušajte ponovo spremiti datoteku.`;
        if (err.status < 500) {
          if (err.message) {
            message = `${file.name}: ${err.message}`;
          }
        }
        this.notificationService.error(message);
        return err;
      })
    );
  }

  private refreshParentList() {
    if (this.itemViewProvider.listViewProvider) {
      this.itemViewProvider.listViewProvider.refresh();
    }
  }
}

const retryAction = <TOut>(
  fn: () => Observable<TOut>,
  retryCount = 3,
  delayMs = 1000
): Observable<TOut> => {
  const t = defer(async () => {
    let err: any;
    for (let i = 0; i < retryCount; i++) {
      try {
        const t = await lastValueFrom(fn());
        return t;
      } catch (e) {
        err = e;
        if (err.status < 500) {
          console.error('Client error, will not retry', e);
          break;
        }
        console.error('Error executing action, retrying', e);
        await new Promise((res) =>
          // Exponential backoff
          setTimeout(res, delayMs * ((i + 1) ^ 2))
        );
      }
    }
    throw err;
  });

  return t;
};
